import { useId } from "react";
import { ArrowRight, Check, Plus } from "lucide-react";

import type { ChildProfile } from "@/domain";
import { MAX_CHILD_PROFILES } from "@/domain/schemas";

import { ProfileAvatar } from "./ProfileAvatar";
import "./profiles.css";

export interface ProfilePickerProps {
  readonly onCreate: () => void;
  readonly onSelect: (profile: ChildProfile) => void;
  readonly profiles: readonly ChildProfile[];
  readonly selectedProfileId: string | null;
}

export function ProfilePicker({
  onCreate,
  onSelect,
  profiles,
  selectedProfileId
}: ProfilePickerProps) {
  const headingId = useId();
  const limitNoticeId = useId();
  const isAtLimit = profiles.length >= MAX_CHILD_PROFILES;

  return (
    <section aria-labelledby={headingId} className="profile-picker">
      <header className="profile-picker__header">
        <div>
          <p className="profile-picker__eyebrow">启航名单</p>
          <h2 id={headingId}>选择小船员</h2>
        </div>
        <strong
          aria-label={
            "已有 " + profiles.length + " 位，最多 " + MAX_CHILD_PROFILES + " 位"
          }
          className="profile-picker__count"
        >
          {profiles.length}/{MAX_CHILD_PROFILES}
        </strong>
      </header>

      <ul className="profile-picker__grid">
        {profiles.map((profile) => {
          const isSelected = profile.id === selectedProfileId;

          return (
            <li key={profile.id}>
              <button
                aria-pressed={isSelected}
                className="profile-picker__card"
                onClick={() => onSelect(profile)}
                type="button"
              >
                <ProfileAvatar avatarId={profile.avatarId} size="large" />
                <span className="profile-picker__nickname">
                  {profile.nickname}
                </span>
                <span className="profile-picker__card-action">
                  {isSelected ? (
                    <>
                      <Check aria-hidden="true" />
                      现在是这位
                    </>
                  ) : (
                    <>
                      <ArrowRight aria-hidden="true" />
                      继续启航
                    </>
                  )}
                </span>
              </button>
            </li>
          );
        })}

        <li>
          <button
            aria-describedby={isAtLimit ? limitNoticeId : undefined}
            className="profile-picker__card profile-picker__card--create"
            disabled={isAtLimit}
            onClick={onCreate}
            type="button"
          >
            <span className="profile-picker__add-icon" aria-hidden="true">
              <Plus />
            </span>
            <span className="profile-picker__nickname">新建小船员</span>
            <span className="profile-picker__card-action">
              {isAtLimit ? "名额已满" : "领取启航证"}
            </span>
          </button>
        </li>
      </ul>

      {isAtLimit ? (
        <p className="profile-picker__limit" id={limitNoticeId} role="status">
          已经有三位小船员了。需要新建时，请让家长先删除一份资料。
        </p>
      ) : null}
    </section>
  );
}
